import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { annaCallRobotClient } from "../api/annaCallRobotClient";
import { getApiProjectId, setApiProjectId } from "../api/projectScope";
import { PRODUCTION_PREFIX } from "../config";
import { parseProjectIdFromPathname } from "../config/paths";
import { useSiteData } from "./SiteDataContext";

export type ProjectOption = { id: number; name: string; paused?: boolean };

export type ProjectContextValue = {
  projects: ProjectOption[];
  projectId: number | null;
  setProjectId: (id: number) => void;
  addProject: (project: ProjectOption) => void;
  removeProject: (id: number) => void;
  ready: boolean;
};

const EMPTY: ProjectContextValue = {
  projects: [],
  projectId: null,
  setProjectId: () => {},
  addProject: () => {},
  removeProject: () => {},
  ready: true,
};

export function parseProjectOption(row: unknown): ProjectOption | null {
  if (!row || typeof row !== "object") {
    return null;
  }
  const candidate = row as { id?: unknown; name?: unknown; paused?: unknown };
  const id = Number(candidate.id);
  if (!Number.isInteger(id) || id <= 0) {
    return null;
  }
  const parsed: ProjectOption = { id, name: String(candidate.name ?? "") };
  if ("paused" in candidate) {
    parsed.paused = Boolean(candidate.paused);
  }
  return parsed;
}

function sortProjects(rows: ProjectOption[]): ProjectOption[] {
  return [...rows].sort((a, b) => {
    const byName = a.name.localeCompare(b.name);
    return byName !== 0 ? byName : a.id - b.id;
  });
}

export function projectIdFromWindowPath(): number | null {
  try {
    return parseProjectIdFromPathname(
      window.location.pathname,
      PRODUCTION_PREFIX,
    );
  } catch {
    return null;
  }
}

export const ProjectContext = createContext<ProjectContextValue>(EMPTY);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { authState } = useSiteData();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectId, setProjectIdState] = useState<number | null>(
    () => projectIdFromWindowPath() ?? getApiProjectId(),
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (authState.isLoading) {
        setReady(false);
        return;
      }
      if (!authState.isAuthenticated) {
        setProjects([]);
        setProjectIdState(null);
        setApiProjectId(null);
        setReady(true);
        return;
      }
      setReady(false);
      try {
        const response = await annaCallRobotClient.listProjects();
        const rows = Array.isArray(response.data?.projects)
          ? response.data.projects
          : [];
        const next: ProjectOption[] = sortProjects(
          rows
            .map((row: unknown) => parseProjectOption(row))
            .filter((row: ProjectOption | null): row is ProjectOption =>
              Boolean(row),
            ),
        );
        if (cancelled) {
          return;
        }
        setProjects(next);
        setProjectIdState((current) => {
          const preferred = projectIdFromWindowPath() ?? current;
          if (preferred != null && next.some((row) => row.id === preferred)) {
            setApiProjectId(preferred);
            return preferred;
          }
          const first = next[0]?.id ?? null;
          setApiProjectId(first);
          return first;
        });
      } catch {
        if (!cancelled) {
          setProjects([]);
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated, authState.isLoading]);

  const setProjectId = useCallback((id: number) => {
    setApiProjectId(id);
    setProjectIdState(id);
  }, []);

  const addProject = useCallback((project: ProjectOption) => {
    setProjects((current) =>
      sortProjects([
        ...current.filter((row) => row.id !== project.id),
        project,
      ]),
    );
  }, []);

  const removeProject = useCallback((id: number) => {
    setProjects((current) => {
      const next = sortProjects(current.filter((row) => row.id !== id));
      setProjectIdState((selected) => {
        if (selected !== id) {
          return selected;
        }
        const fallback = next[0]?.id ?? null;
        setApiProjectId(fallback);
        return fallback;
      });
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      projects,
      projectId,
      setProjectId,
      addProject,
      removeProject,
      ready,
    }),
    [projects, projectId, setProjectId, addProject, removeProject, ready],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextValue {
  return useContext(ProjectContext);
}
